import mediator from '../utilities/mediator';
import log from '../utilities/log';
import { EntityType } from 'adc-sirius-module-common';

import {
  ApiErrorResponse,
  Device,
  DeviceEntityType,
  StudyDataRecord,
} from '../models';
import {
  mutationOptions,
  queryOptions,
  UseMutationOptions,
  UseQueryOptions,
} from '@tanstack/react-query';

type DevicesQueryParams = {
  key?: string;
};

type UpdateDeviceVariables = {
  deviceKey: Device['key'];
  updatedDeviceValues: Partial<Device>;
};

type AttributeDeviceVariables = {
  assignedKeys: string[];
  unassignedKeys: string[];
  entityType: DeviceEntityType;
  entityKey: string;
};

export function devicesByStudyQueryOptions(
  params: DevicesQueryParams,
  options?: Partial<UseQueryOptions<Device[]>>,
) {
  return devicesQueryOptions(EntityType.Study, params, options);
}

export function devicesBySiteQueryOptions(
  params: DevicesQueryParams,
  options?: Partial<UseQueryOptions<Device[]>>,
) {
  return devicesQueryOptions(EntityType.StudySite, params, options);
}

export function devicesBySubjectQueryOptions(
  params: DevicesQueryParams,
  options?: Partial<UseQueryOptions<Device[]>>,
) {
  return devicesQueryOptions(EntityType.StudySubject, params, options);
}

function devicesQueryOptions(
  entityType: DeviceEntityType,
  params: DevicesQueryParams,
  options?: Partial<UseQueryOptions<Device[]>>,
) {
  const eventByEntityType = {
    [EntityType.Study]: 'device:getDevicesByStudy',
    [EntityType.StudySite]: 'device:getDevicesBySite',
    [EntityType.StudySubject]: 'device:getDevicesBySubject',
  } as const;

  return queryOptions<Device[]>({
    ...options,
    queryKey: ['devices', entityType, params],
    queryFn: async () => {
      try {
        return await mediator.request<Device[]>(eventByEntityType[entityType], {
          ...params,
        });
      } catch (error: any) {
        log.error(error);
        throw new ApiErrorResponse(error.status, error.message);
      }
    },
  });
}

export function deviceByKeyQueryOptions(
  deviceKey: string,
  options?: Partial<UseQueryOptions<Device>>,
) {
  return queryOptions<Device>({
    ...options,
    queryKey: ['device', deviceKey],
    queryFn: async () => {
      try {
        return await mediator.request<Device>('device:getDeviceByKey', {
          key: deviceKey,
        });
      } catch (error: any) {
        log.error(error);
        throw new ApiErrorResponse(error.status, error.message);
      }
    },
    enabled: !!deviceKey,
  });
}

export function availableDevicesQueryOptions(
  options?: Partial<UseQueryOptions<Device[]>>,
) {
  return queryOptions<Device[]>({
    ...options,
    queryKey: ['availableDevices'],
    queryFn: async () => {
      try {
        return await mediator.request<Device[]>('device:getAvailableDevices');
      } catch (error: any) {
        log.error(error);
        throw new ApiErrorResponse(error.status, error.message);
      }
    },
  });
}

export function deviceUploadHistoryQueryOptions(
  params: DevicesQueryParams,
  options?: Partial<UseQueryOptions<StudyDataRecord[]>>,
) {
  return queryOptions<StudyDataRecord[]>({
    ...options,
    queryKey: ['deviceUploadHistory', params],
    queryFn: async () => {
      try {
        return await mediator.request<StudyDataRecord[]>(
          'device:getDeviceUploadHistory',
          {
            ...params,
          },
        );
      } catch (error: any) {
        log.error(error);
        throw new ApiErrorResponse(error.status, error.message);
      }
    },
    enabled: !!params?.key,
  });
}

export function updateDeviceMutationOptions(
  options?: Partial<UseMutationOptions<Device, Error, UpdateDeviceVariables>>,
) {
  return mutationOptions<Device, Error, UpdateDeviceVariables>({
    ...options,
    mutationFn: async ({ updatedDeviceValues }: UpdateDeviceVariables) => {
      try {
        return await mediator.request<Device>('device:updateDevice', {
          data: updatedDeviceValues,
        });
      } catch (error: any) {
        log.error(error);
        throw new ApiErrorResponse(error.status, error.message);
      }
    },
  });
}

export function attributeDeviceMutationOptions(
  options?: Partial<
    UseMutationOptions<Device[], Error, AttributeDeviceVariables>
  >,
) {
  return mutationOptions<Device[], Error, AttributeDeviceVariables>({
    ...options,
    mutationFn: async (values: AttributeDeviceVariables) => {
      try {
        return await mediator.request<Device[]>('device:attributeDevices', {
          data: values,
        });
      } catch (error: any) {
        log.error(error);
        throw new ApiErrorResponse(error.status, error.message);
      }
    },
  });
}
